import {
  UIEvent as ReactUIEvent,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import { useOnResize } from "./useResizeObserver";

export type ScrollPosition = [scrollTop: number, maxScrollTop: number];

export const useScrollPosition = (): [
  ScrollPosition,
  {
    onScroll: (ev: ReactUIEvent<HTMLElement, UIEvent>) => void;
    ref: (instance: HTMLElement | null) => void;
  },
] => {
  const [position, setPosition] = useState<ScrollPosition>([0, 0]);
  const isMounted = useRef(false);
  const update = useCallback((target: HTMLElement) => {
    requestAnimationFrame(() => {
      if (!isMounted.current) {
        return;
      }
      const newST = Math.round(target.scrollTop);
      const newMax = target.scrollHeight - target.offsetHeight;
      setPosition((current) =>
        current[0] === newST && current[1] === newMax
          ? current
          : [newST, newMax],
      );
    });
  }, []);
  const resizeRef = useOnResize(({ target }) => update(target as HTMLElement));
  const props = useMemo(() => {
    return {
      ref(instance: HTMLElement | null) {
        isMounted.current = Boolean(instance);
        resizeRef(instance);
        if (instance) {
          update(instance);
        }
      },
      onScroll(ev: ReactUIEvent<HTMLElement, UIEvent>) {
        update(ev.target as HTMLElement);
      },
    };
  }, [resizeRef, update]);

  return [position, props];
};
